import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { VideoCallRoom } from "@/components/VideoCallRoom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowLeft, AlertCircle, Video } from "lucide-react";
import { format } from "date-fns";

interface Session {
  id: string;
  scheduled_at: string;
  duration_minutes: number;
  video_room_url: string | null;
  status: string;
  mentor_id: string;
  student_id: string;
  mentors?: {
    first_name: string;
    last_name: string;
    user_id?: string | null;
    email?: string | null;
  };
  profiles?: {
    first_name: string;
    last_name: string;
  };
}

const SessionRoom = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [roomRefreshKey, setRoomRefreshKey] = useState(0);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/auth");
          return;
        }
        setCurrentUser(user);

        const { data, error: fetchError } = await supabase
          .from("sessions")
          .select(`
            *,
            mentors:mentor_id (
              first_name,
              last_name,
              user_id,
              email
            ),
            profiles:student_id (
              first_name,
              last_name
            )
          `)
          .eq("id", sessionId)
          .single();

        if (fetchError) throw fetchError;

        if (!data) {
          setError("Session not found");
          setLoading(false);
          return;
        }

        // Check if user is authorized (either mentor or student)
        const mentorUserId = data.mentors?.user_id;
        const mentorEmail = data.mentors?.email;
        const isStudent = data.student_id === user.id;
        const isMentor = mentorUserId === user.id || (!!mentorEmail && mentorEmail === user.email);
        
        if (!isStudent && !isMentor) {
          setError("You are not authorized to join this session");
          setLoading(false);
          return;
        }

        // If no video room exists, create one on-demand
        if (!data.video_room_url) {
          console.log('No video room found, creating one...');
          const mentorName = data.mentors ? `${data.mentors.first_name} ${data.mentors.last_name}` : 'Mentor';
          const studentName = data.profiles ? `${data.profiles.first_name} ${data.profiles.last_name}` : 'Student';
          
          const { data: roomData, error: roomError } = await supabase.functions.invoke('create-daily-room', {
            body: {
              sessionId: sessionId,
              mentorName,
              studentName,
              scheduledAt: data.scheduled_at,
            }
          });

          if (roomError) {
            console.error('Failed to create video room:', roomError);
            setError("Failed to create video room. Please try again or contact support.");
            setLoading(false);
            return;
          }

          // Fetch session again to get the updated video_room_url
          const { data: updatedSession, error: refetchError } = await supabase
            .from("sessions")
            .select(`
              *,
              mentors:mentor_id (
                first_name,
                last_name,
                user_id,
                email
              ),
              profiles:student_id (
                first_name,
                last_name
              )
            `)
            .eq("id", sessionId)
            .single();

          if (refetchError || !updatedSession) {
            setError("Failed to load session after creating room");
            setLoading(false);
            return;
          }

          setSession(updatedSession);
        } else {
          setSession(data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching session:", err);
        setError(err instanceof Error ? err.message : "Failed to load session");
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId, navigate]);

  const handleLeaveCall = () => {
    const userRole = currentUser?.user_metadata?.role;
    if (userRole === 'mentor') {
      navigate('/dashboard/mentor');
    } else if (userRole === 'student') {
      navigate('/dashboard/student');
    } else {
      navigate('/');
    }
  };

  const handleJoinError = async (err: any) => {
    const type = err?.error?.type || err?.type || '';
    const msg = err?.errorMsg || '';
    if (String(type).includes('not-allowed') || String(msg).toLowerCase().includes('not allowed')) {
      try {
        setLoading(true);
        if (!session) return;
        const mentorName = session.mentors ? `${session.mentors.first_name} ${session.mentors.last_name}` : 'Mentor';
        const studentName = session.profiles ? `${session.profiles.first_name} ${session.profiles.last_name}` : 'Student';
        await supabase.functions.invoke('create-daily-room', {
          body: {
            sessionId: sessionId,
            mentorName,
            studentName,
            scheduledAt: session.scheduled_at,
          }
        });
        const { data: updatedSession } = await supabase
          .from('sessions')
          .select(`
            *,
            mentors:mentor_id (
              first_name,
              last_name,
              user_id
            ),
            profiles:student_id (
              first_name,
              last_name
            )
          `)
          .eq('id', sessionId)
          .single();
        if (updatedSession) {
          setSession(updatedSession as Session);
          setRoomRefreshKey((k) => k + 1);
          setError(null);
        }
      } catch (recoveryErr) {
        console.error('Room recovery failed:', recoveryErr);
        setError('Could not recover video room. Please try again or contact support.');
      } finally {
        setLoading(false);
      }
    }
  };
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Unable to Join Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error || "Session not found"}</AlertDescription>
            </Alert>
            <Button 
              onClick={() => {
                const userRole = currentUser?.user_metadata?.role;
                if (userRole === 'mentor') {
                  navigate('/dashboard/mentor');
                } else if (userRole === 'student') {
                  navigate('/dashboard/student');
                } else {
                  navigate('/');
                }
              }} 
              className="w-full"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const userName = currentUser?.user_metadata?.first_name || 
                   currentUser?.email?.split('@')[0] || 
                   'User';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLeaveCall}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-lg font-semibold flex items-center gap-2">
                  <Video className="h-5 w-5 text-primary" />
                  Session with {session.mentors?.first_name} {session.mentors?.last_name}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(session.scheduled_at), "MMMM d, yyyy 'at' h:mm a")} • {session.duration_minutes} minutes
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Call Container */}
      <div className="container mx-auto px-4 py-6">
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <VideoCallRoom
              key={roomRefreshKey}
              roomUrl={session.video_room_url!}
              userName={userName}
              onJoinError={handleJoinError}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SessionRoom;
